/**
 * Catálogo de conteúdo pra popular o catálogo de Serviços por especialidade odontológica.
 * A tabela `Specialty` (packages/db) só guarda code/label pro FK — o conteúdo dos presets fica
 * aqui em código.
 *
 * Lista única por especialidade: até 2026-08 isso era `{ services, procedures }` (dois conjuntos
 * pro mesmo conceito, com nomes divergentes — "Limpeza (profilaxia)" vs "Limpeza / Profilaxia"),
 * fundido junto com a unificação de OfferedService × ProcedureType.
 *
 * Nome específico em vez de genérico é regra, não estilo: o seed deduplica por nome, então um
 * "Avaliação" genérico repetido em 7 especialidades viraria UM item só no catálogo, com a cor de
 * qualquer especialidade que tenha sido ativada primeiro. Clínico Geral é a exceção deliberada —
 * é a especialidade guarda-chuva, e lá "Avaliação"/"Cirurgia" genéricos fazem sentido.
 */

export const SPECIALTY_CODES = [
  "CLINICO_GERAL",
  "ORTODONTIA",
  "ENDODONTIA",
  "IMPLANTODONTIA",
  "PERIODONTIA",
  "ODONTOPEDIATRIA",
  "CIRURGIA_BUCOMAXILO",
  "PROTESE_DENTARIA",
  "DENTISTICA",
  "HARMONIZACAO_OROFACIAL",
  "DTM_DOR_OROFACIAL",
  "ESTOMATOLOGIA",
  "RADIOLOGIA",
  "ODONTOGERIATRIA",
  "PACIENTES_ESPECIAIS",
  "ORTOPEDIA_FUNCIONAL",
  "PATOLOGIA_ORAL",
  "PROTESE_BUCOMAXILOFACIAL",
  "ODONTOLOGIA_DO_ESPORTE",
  "ODONTOLOGIA_DO_TRABALHO",
  "ODONTOLOGIA_HOSPITALAR",
  "ODONTOLOGIA_LEGAL",
  "SAUDE_COLETIVA",
  "ACUPUNTURA",
  "HOMEOPATIA",
  "CIRURGIA_ESTETICA_OROFACIAL",
] as const;

export type SpecialtyCode = (typeof SPECIALTY_CODES)[number];

export const SPECIALTY_LABELS: Record<SpecialtyCode, string> = {
  CLINICO_GERAL: "Clínico Geral",
  ORTODONTIA: "Ortodontia",
  ENDODONTIA: "Endodontia",
  IMPLANTODONTIA: "Implantodontia",
  PERIODONTIA: "Periodontia",
  ODONTOPEDIATRIA: "Odontopediatria",
  CIRURGIA_BUCOMAXILO: "Cirurgia Bucomaxilofacial",
  PROTESE_DENTARIA: "Prótese Dentária",
  DENTISTICA: "Dentística",
  HARMONIZACAO_OROFACIAL: "Harmonização Orofacial",
  DTM_DOR_OROFACIAL: "Disfunção Temporomandibular e Dor Orofacial",
  ESTOMATOLOGIA: "Estomatologia",
  RADIOLOGIA: "Radiologia Odontológica e Imaginologia",
  ODONTOGERIATRIA: "Odontogeriatria",
  PACIENTES_ESPECIAIS: "Odontologia para Pacientes com Necessidades Especiais",
  ORTOPEDIA_FUNCIONAL: "Ortopedia Funcional dos Maxilares",
  PATOLOGIA_ORAL: "Patologia Oral e Maxilofacial",
  PROTESE_BUCOMAXILOFACIAL: "Prótese Bucomaxilofacial",
  ODONTOLOGIA_DO_ESPORTE: "Odontologia do Esporte",
  ODONTOLOGIA_DO_TRABALHO: "Odontologia do Trabalho",
  ODONTOLOGIA_HOSPITALAR: "Odontologia Hospitalar",
  ODONTOLOGIA_LEGAL: "Odontologia Legal",
  SAUDE_COLETIVA: "Saúde Coletiva",
  ACUPUNTURA: "Acupuntura",
  HOMEOPATIA: "Homeopatia",
  CIRURGIA_ESTETICA_OROFACIAL: "Cirurgia Estética Orofacial",
};

/** Especialidade pré-selecionada por padrão no onboarding — a mais genérica/comum. */
export const DEFAULT_SPECIALTY_CODE: SpecialtyCode = "CLINICO_GERAL";

export type SpecialtyServicePreset = {
  name: string;
  priceCents: number;
  durationMinutes: number;
  /** Cor do card na agenda quando esse serviço é escolhido. Famílias semânticas por tipo de
   *  trabalho (azul/ciano = consulta e avaliação, verde = prevenção e periodontia, teal =
   *  dentística, vermelho = cirurgia, âmbar = endodontia, rosa = ortodontia, índigo/violeta = prótese)
   *  — com tons bem separados entre os que costumam aparecer juntos num plano de tratamento
   *  pra ficarem distinguíveis lado a lado num mesmo dia. */
  color: string;
  /** Cobrado por dente: no plano de tratamento, o preço multiplica pela quantidade de dentes. */
  cobradoPorDente?: boolean;
};

/** Presets do botão "Preencher" de Serviços (as 8 especialidades originais). A base de mercado
 *  (BaseProcedimento) cobre todas — é dela que o onboarding da clínica copia. */
export const SPECIALTY_PRESETS: Partial<Record<SpecialtyCode, SpecialtyServicePreset[]>> = {
  CLINICO_GERAL: [
    { name: "Consulta", priceCents: 0, durationMinutes: 30, color: "#3B82F6" },
    { name: "Avaliação", priceCents: 0, durationMinutes: 30, color: "#06B6D4" },
    { name: "Limpeza (profilaxia)", priceCents: 0, durationMinutes: 45, color: "#10B981" },
    { name: "Aplicação de flúor", priceCents: 0, durationMinutes: 30, color: "#0EA5E9" },
    { name: "Restauração", priceCents: 0, durationMinutes: 60, color: "#14B8A6", cobradoPorDente: true },
    { name: "Extração", priceCents: 0, durationMinutes: 60, color: "#EF4444", cobradoPorDente: true },
    { name: "Cirurgia", priceCents: 0, durationMinutes: 120, color: "#DC2626" },
    { name: "Clareamento dental", priceCents: 0, durationMinutes: 60, color: "#EAB308" },
    { name: "Radiografia periapical", priceCents: 0, durationMinutes: 15, color: "#64748B" },
  ],
  ORTODONTIA: [
    { name: "Avaliação ortodôntica", priceCents: 0, durationMinutes: 30, color: "#06B6D4" },
    { name: "Instalação de aparelho fixo", priceCents: 0, durationMinutes: 90, color: "#DB2777" },
    { name: "Manutenção ortodôntica", priceCents: 0, durationMinutes: 30, color: "#EC4899" },
    { name: "Remoção de aparelho", priceCents: 0, durationMinutes: 45, color: "#F472B6" },
    { name: "Contenção ortodôntica", priceCents: 0, durationMinutes: 30, color: "#BE185D" },
  ],
  ENDODONTIA: [
    { name: "Avaliação endodôntica", priceCents: 0, durationMinutes: 30, color: "#06B6D4" },
    { name: "Tratamento de canal (unirradicular)", priceCents: 0, durationMinutes: 90, color: "#F59E0B", cobradoPorDente: true },
    { name: "Tratamento de canal (multirradicular)", priceCents: 0, durationMinutes: 120, color: "#D97706", cobradoPorDente: true },
    { name: "Retratamento endodôntico", priceCents: 0, durationMinutes: 120, color: "#B45309", cobradoPorDente: true },
  ],
  IMPLANTODONTIA: [
    { name: "Avaliação para implante", priceCents: 0, durationMinutes: 30, color: "#06B6D4" },
    { name: "Cirurgia de implante unitário", priceCents: 0, durationMinutes: 90, color: "#0891B2", cobradoPorDente: true },
    { name: "Enxerto ósseo", priceCents: 0, durationMinutes: 90, color: "#A16207" },
    { name: "Instalação de prótese sobre implante", priceCents: 0, durationMinutes: 60, color: "#C026D3", cobradoPorDente: true },
  ],
  PERIODONTIA: [
    { name: "Avaliação periodontal", priceCents: 0, durationMinutes: 30, color: "#06B6D4" },
    { name: "Raspagem e alisamento radicular", priceCents: 0, durationMinutes: 60, color: "#059669" },
    { name: "Cirurgia periodontal", priceCents: 0, durationMinutes: 90, color: "#047857" },
    { name: "Manutenção periodontal", priceCents: 0, durationMinutes: 30, color: "#10B981" },
  ],
  ODONTOPEDIATRIA: [
    { name: "Consulta odontopediátrica", priceCents: 0, durationMinutes: 30, color: "#3B82F6" },
    { name: "Aplicação de selante", priceCents: 0, durationMinutes: 30, color: "#84CC16", cobradoPorDente: true },
    { name: "Restauração em dente decíduo", priceCents: 0, durationMinutes: 45, color: "#14B8A6", cobradoPorDente: true },
    { name: "Orientação de higiene bucal", priceCents: 0, durationMinutes: 20, color: "#4ADE80" },
    // Mesmo item de CLINICO_GERAL — categoria idêntica de propósito, senão o resultado do dedupe
    // dependeria de qual especialidade foi ativada primeiro.
    { name: "Aplicação de flúor", priceCents: 0, durationMinutes: 30, color: "#0EA5E9" },
  ],
  CIRURGIA_BUCOMAXILO: [
    { name: "Avaliação cirúrgica", priceCents: 0, durationMinutes: 30, color: "#06B6D4" },
    { name: "Extração de siso", priceCents: 0, durationMinutes: 60, color: "#EF4444", cobradoPorDente: true },
    { name: "Cirurgia de freio lingual/labial", priceCents: 0, durationMinutes: 45, color: "#DC2626" },
    { name: "Biópsia oral", priceCents: 0, durationMinutes: 45, color: "#B91C1C" },
  ],
  PROTESE_DENTARIA: [
    { name: "Avaliação protética", priceCents: 0, durationMinutes: 30, color: "#06B6D4" },
    { name: "Prótese total", priceCents: 0, durationMinutes: 60, color: "#2457FF" },
    { name: "Prótese parcial removível", priceCents: 0, durationMinutes: 60, color: "#475569" },
    { name: "Coroa protética", priceCents: 0, durationMinutes: 60, color: "#8B5CF6", cobradoPorDente: true },
    { name: "Prótese sobre implante", priceCents: 0, durationMinutes: 60, color: "#6366F1" },
  ],
};
